import { STATUS_TYPES } from "src/components/collections/consts";

export const PAGE_SIZE_OPTIONS = [10, 20, 50];

export const DEFAULT_PAGINATION_MODEL = { page: 0, pageSize: 20 };

export const EXPLORE_FILTERS_QUERY_PARAM = "filters";
export const FILTER_DEBOUNCE_MS = 300;

export const GRAPHQL_KINDS = {
  SCALAR: "SCALAR",
  LIST: "LIST",
  OBJECT: "OBJECT",
  NON_NULL: "NON_NULL",
  ENUM: "ENUM"
};

export const SCALAR_NAMES = {
  STRING: "String",
  BOOLEAN: "Boolean",
  INT: "Int",
  FLOAT: "Float",
  JSON: "JSON",
  MULTI_LANGUAGE_STRING: "MultiLanguageString",
  ACTION: "Action",
  ID: "ID"
};

export const SORTABLE_SCALAR_NAMES = new Set([
  SCALAR_NAMES.STRING,
  SCALAR_NAMES.INT,
  SCALAR_NAMES.FLOAT,
  SCALAR_NAMES.BOOLEAN,
  SCALAR_NAMES.ID,
  SCALAR_NAMES.MULTI_LANGUAGE_STRING
]);

export const SORT_INPUT_DIRECTIONS = { asc: "ASC", desc: "DESC" };

export const EMPTY_SORT_MODEL = [];
export const SERVER_ERROR_STATUS = 500;

export const VERSION_DATE_FORMAT = "lll";
export const VERSION_LABEL_PREFIX = "Version: ";
export const VERSIONS_LABEL = "Versions";
export const NO_VERSIONS_TEXT = "No versions available yet";
export const LIVE_VERSION_LABEL = "Live";

export const KNOWLEDGE_SOURCES_VALIDATION_PREFIX = "knowledge-sources";

// In the server's precedence (and ascending sort) order.
export const ROW_STATUS_LABELS = {
  INVALID: STATUS_TYPES.INVALID,
  UNASSIGNED: STATUS_TYPES.UNASSIGNED,
  PENDING: STATUS_TYPES.PENDING,
  LIVE: STATUS_TYPES.LIVE
};

export const STATUS_COLUMN = { FIELD: "status", HEADER: "Status", WIDTH: 130 };

export const ROW_LEVEL_PATH_LEAF = "root";
export const CELL_ERROR_CLASS = "error-cell";

export const YES_LABEL = "Yes";
export const NO_LABEL = "No";
export const BOOLEAN_EDIT_OPTIONS = [
  { key: true, display_key: YES_LABEL },
  { key: false, display_key: NO_LABEL }
];
export const EMPTY_VALUE_PLACEHOLDER = "—";

export const EXPLORE_COPY = {
  MISSING_SCHEMA_TYPE: "This knowledge source is missing a schema type and cannot be explored.",
  NO_ROWS_TEXT: "No data here yet",
  NO_RESULTS_TEXT: "No results found",
  NO_RESULTS_DESCRIPTION: "Check your filters and try again"
};

export const ROW_DRAWER_COPY = {
  CLOSE_BUTTON: "Close",
  DEFAULT_RECORD_NAME: "Record"
};

export const ID_FIELD_SUFFIX = "_id";

// "Source Type" CHOOSE_ONE on the knowledge-source spec. Editing rows in Explore is only
// allowed when it's set to "Manual Input"; the "Integration" option syncs from an external system.
export const SOURCE_TYPE_FIELD_KEY = "edb3bd32-9ce7-4434-a291-ecb3f0b14c0c";
export const MANUAL_INPUT_OPTION_KEY = "2ca61c5e-a30d-4ef6-9a8a-494a363fb8a8";

export const ADD_ROW_DISABLED_TOOLTIP =
  "Adding rows is available only for Manual Input sources";

export const DRAWER_MODES = {
  VIEW: "view",
  EDIT: "edit",
  CREATE: "create"
};

export const ROW_DRAWER_EDIT_COPY = {
  CREATE_TITLE_PREFIX: "Add ",
  EDIT_TITLE_SUFFIX: "details",
  CANCEL_BUTTON: "Cancel"
};

export const AUTO_SAVE_STATUS = {
  IDLE: "idle",
  SAVING: "saving",
  SAVED: "saved"
};

export const AUTO_SAVE_COPY = {
  [AUTO_SAVE_STATUS.IDLE]: "Auto-save",
  [AUTO_SAVE_STATUS.SAVING]: "Saving changes",
  [AUTO_SAVE_STATUS.SAVED]: "All changes saved"
};

export const AUTO_SAVE_INDICATOR_TEST_ID = "explore-auto-save-indicator";

export const MANDATORY_FIELD_ERROR = "This field is mandatory. Please enter a value for this field.";

export const SAVE_ERROR_MESSAGE = "Couldn't save your changes. Please try again.";

export const SAVE_ERROR_SEVERITY = "error";

export const ROW_ACTIONS = {
  EDIT: "Edit",
  DUPLICATE: "Duplicate",
  DELETE: "Delete"
};

export const EMPTY_ROWS_DIALOG_COPY = {
  description: (count) => `By leaving this page, ${count} empty ${count === 1 ? "row" : "rows"} will be deleted. Are you sure you want to proceed?`
};

export const DELETE_ROW_DIALOG_COPY = {
  TITLE: "Delete selected rows?",
  DESCRIPTION: "This can't be undone. Changes in this table are saved automatically, so the selected rows will be removed permanently.",
  CONFIRM: "Delete",
  CANCEL: "Cancel"
};

export const BULK_FEEDBACK_SEVERITY = {
  SUCCESS: "success",
  ERROR: "error"
};

export const BULK_EDIT_FEEDBACK = {
  updatedWithFailures: (ok, failed) => `${ok} updated, ${failed} failed`,
  allFailed: (failed) => `Couldn't update ${failed} row${failed === 1 ? "" : "s"}. Please try again.`,
  deleted: (ok) => `${ok} row${ok === 1 ? "" : "s"} deleted`,
  deleteFailed: "Couldn't delete the selected rows. Please try again."
};

export const BULK_UPLOAD_COPY = {
  ERROR: "Bulk upload failed. Please check your file and try again.",
  READ_ERROR: "Could not read file",
  REFS_ERROR: "Could not load reference options. Please try again."
};

export const DOWNLOAD_COPY = {
  BUTTON: "Download",
  ERROR: "Download failed. Please try again."
};

export const ENTITY_LABEL_FIELD_NAMES = ["name", "full_name", "title"];
export const ENTITY_OPTIONS_PAGE_SIZE = 50;

export const ENTITY_LABELS_FETCH_LIMIT = 1000;

export const REF_VERSION_CALL_MARKER = "(version: $version)";

export const CONFIG_REF_SCALARS = {
  SKILL: "SkillId",
  KNOWLEDGE_ENTRY: "KnowledgeEntryId"
};
export const CONFIG_REF_SCALAR_NAMES = new Set(Object.values(CONFIG_REF_SCALARS));

export const REF_KINDS = {
  ENTITY: "entity",
  CONFIG: "config"
};

export const REF_SELECT_COPY = {
  NO_OPTIONS: "No options found"
};

export const REF_CELL_LOADING_TEST_ID = "ref-cell-loading";

export const REF_SELECT_TEST_ID = "explore-ref-select";

export const ASSOCIATION_COPY = {
  ADD_PREFIX: "Add ",
  SEARCH_PLACEHOLDER: "Search",
  NO_OPTIONS: "No matches",
  DELETE_TOOLTIP: "Delete",
  emptyList: (entityLabel) => `No ${entityLabel} yet`,
  emptyEntityTooltip: (label) => `Upload ${label} knowledge source in order to add ${label.toLowerCase()}`,
  itemTitle: (label, index) => `${label} ${index + 1}`,
  listTitle: (label, count) => `${label} (${count})`,
  duplicateEntity: (entityLabel, itemLabel) => `${entityLabel} already used by another ${itemLabel}`,
  allEntitiesUsedTooltip: (entityLabel) => `All ${entityLabel} are already used`,
  LOAD_ERROR: "Couldn't load this field. Please try again.",
  SAVE: "Save",
  CANCEL: "Cancel"
};

export const UNSAVED_ASSOCIATION_DIALOG_COPY = {
  TITLE: "Discard changes?",
  DESCRIPTION: "Your changes to this list haven't been saved.",
  CONFIRM: "Discard",
  CANCEL: "Keep editing"
};

export const ASSOCIATION_LINK_EDITOR_WIDTH = 320;
export const ASSOCIATION_ERROR_POPPER_OFFSET = [0, 4];
export const ASSOCIATION_ITEM_SKELETON_HEIGHT = 48;
export const ASSOCIATION_MAX_ITEM_SKELETONS = 3;
export const ASSOCIATION_LINK_SKELETON_LABEL_WIDTHS = ["70%", "55%", "80%", "60%", "45%"];
export const ASSOCIATION_LIST_EDITOR_WIDTH = 480;

export const ASSOCIATION_EDITOR_POPOVER_PROPS = {
  disableRestoreFocus: true,
  elevation: 3,
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  sx: { mt: 0.5 }
};

export const UNIQUE_BY_ENTITY_ASSOCIATIONS = new Set(["outcome"]);

export const UNIQUE_VIOLATION_CODE = "UNIQUE_VIOLATION";

export const COLLAPSIBLE_VARIANTS = { FILLED: "filled", OUTLINED: "outlined" };

export const MAPPING_UPLOAD_COPY = {
  MENU_TITLE: "Upload mapping",
  MENU_DESCRIPTION: "Upload mapping connection from CSV",
  DIALOG_TITLE: "Mapping upload",
  DOWNLOAD_MENU_BUTTON: "Mapping table",
  DOWNLOAD_TEMPLATE: "Download mapping template",
  DOWNLOAD_DATA: "Download mapping data",
  TEMPLATE_FILE_NAME: "Mapping template",
  disabledTooltip: (entityLabel) => `Add ${entityLabel} first to upload`,
  success: (count) => `${count} mapping changes applied successfully`
};
